import { Trophy } from 'lucide-preact'
import { memo } from 'preact/compat'
import type { HubSpotDealAnalytics } from '@/types/hubspot'
import { formatMoney } from '@/utils/money'

interface ClosedWonStatTileProps {
    data: HubSpotDealAnalytics | null
    loading: boolean
    error: string | null
}

export const ClosedWonStatTile = memo(({ data, loading, error }: ClosedWonStatTileProps) => {
    if (loading) {
        return (
            <div className="tile">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex flex-col gap-1">
                        <div className="h-3 w-24 rounded-md bg-shimmer-light animate-shimmer" />
                        <div className="h-3 w-20 rounded-md bg-shimmer-light animate-shimmer" />
                    </div>
                    <span className="w-5 h-5 rounded-md bg-shimmer-light animate-shimmer" />
                </div>
                <div className="flex items-end gap-4 mt-auto">
                    <div className="flex-1">
                        <div className="h-8 w-28 mb-2 rounded-md bg-shimmer-light animate-shimmer" />
                        <div className="h-3 w-24 rounded-md bg-shimmer-light animate-shimmer" />
                    </div>
                </div>
            </div>
        )
    }

    const closedWon = data?.closedWon
    // Without a CRM a £0 measures nothing. `=== false` because no data at all is "No data", not this.
    const noCrm = data?.crmConnected === false
    // Pounds and euros added together make a figure in neither, so no total is shown.
    const mixedCurrencies = data?.mixedCurrencies === true

    return (
        <div className="tile">
            <div className="flex items-start justify-between mb-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
                    <span className="block">Closed</span>
                    <span className="block">Won</span>
                </h3>
                <Trophy className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
            {!error && noCrm && (
                <div className="text-sm text-slate-500 dark:text-slate-400">No CRM connected</div>
            )}
            {!error && !noCrm && closedWon && (
                <div className="flex items-end justify-between gap-4">
                    <div>
                        {mixedCurrencies ? (
                            <div className="text-sm text-slate-500 dark:text-slate-400">Mixed currencies</div>
                        ) : (
                            <div className="text-3xl font-bold text-green-600 dark:text-green-400 leading-none">
                                {formatMoney(closedWon.total, data?.currency, { compact: true })}
                            </div>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                                {closedWon.count} {closedWon.count === 1 ? 'deal' : 'deals'}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            {!error && !noCrm && !closedWon && (
                <div className="text-sm text-slate-500 dark:text-slate-400">No data</div>
            )}
        </div>
    )
})
